/*
 * tee-preview-enhancements.js
 * --------------------------------------------------------------------------
 * Enhancements for the teeinblue live preview (#tee-gallery) inside
 * .product__media-wrapper:
 *
 *   1. Desktop hover magnifier   (>= 1024px, hover + fine pointer)
 *   2. Mobile tap-to-zoom        (< 1024px, "inzoom voor telefoon")
 *   3. Mobile collapse/expand    curtain handle for the sticky preview
 *      (pairs with tee-sticky-gallery.js)
 *
 * Design notes:
 * - teeinblue renders the preview as a layered composite — an <img> mockup
 *   plus SVG artwork inside .tee-mockup-zoomable > .tee-zoom-normal. We zoom by
 *   transforming .tee-zoom-normal (vector artwork stays crisp), never a canvas
 *   or a flat image.
 * - teeinblue is a Vue app that re-renders the preview, so all interaction is
 *   bound via EVENT DELEGATION on the stable theme wrapper (.product__media-
 *   wrapper). Affordances are CSS (pseudo-elements), so nothing we add gets
 *   wiped on re-render. Transforms are set inline only transiently and always
 *   reset, so teeinblue's own inline styles are never clobbered.
 * - Mirrors the "wait for the wrapper, observe body" pattern from
 *   tee-gallery-height-sync.js.
 * --------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var WRAPPER_SEL = '.product__media-wrapper';
  var ZOOMABLE_SEL = '.tee-mockup-zoomable';
  var INNER_SEL = '.tee-zoom-normal';

  var MAGNIFY_SCALE = 2;   // desktop hover
  var ZOOM_SCALE = 2.4;    // mobile tap

  var desktopMQ = window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)');
  var mobileMQ = window.matchMedia('(max-width: 1023.98px)');

  function clamp(v, min, max) {
    return v < min ? min : v > max ? max : v;
  }

  // iOS / iPadOS (incl. iPadOS 13+ which reports as "MacIntel" but is touch).
  function isIOS() {
    var ua = navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  function init() {
    var wrapper = document.querySelector(WRAPPER_SEL);
    if (!wrapper) return;

    setupMagnifier(wrapper);
    // Tap-to-zoom is disabled on iOS (per request). The desktop hover magnifier
    // never runs on iOS anyway (it requires hover + fine pointer + >=1024px).
    if (isIOS()) {
      document.documentElement.classList.add('tee-no-tap-zoom'); // CSS hides the badge
    } else {
      setupMobileZoom(wrapper);
    }
    setupCurtain(wrapper);
  }

  /* ----------------------------------------------------------------------
   * 1. Desktop hover magnifier
   * -------------------------------------------------------------------- */
  function setupMagnifier(wrapper) {
    var activeZoomable = null;
    var pendingX = 0, pendingY = 0, pendingZoomable = null;
    var rafId = 0;

    function reset() {
      if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
      pendingZoomable = null;
      if (!activeZoomable) return;
      var inner = activeZoomable.querySelector(INNER_SEL);
      if (inner) {
        inner.style.removeProperty('transform');
        inner.style.removeProperty('transform-origin');
      }
      activeZoomable.classList.remove('tee-magnify-active');
      activeZoomable = null;
    }

    // Without permanent compositing, every transform write re-rasterizes the
    // preview (that's what keeps it sharp). Batch writes to one per frame so the
    // re-paints stay smooth even as the cursor moves quickly.
    function applyPending() {
      rafId = 0;
      var zoomable = pendingZoomable;
      if (!zoomable || !document.contains(zoomable)) return;
      var inner = zoomable.querySelector(INNER_SEL);
      if (!inner) return;

      var r = zoomable.getBoundingClientRect();
      var x = clamp(((pendingX - r.left) / r.width) * 100, 0, 100);
      var y = clamp(((pendingY - r.top) / r.height) * 100, 0, 100);

      inner.style.transformOrigin = x + '% ' + y + '%';
      inner.style.transform = 'scale(' + MAGNIFY_SCALE + ')';
      zoomable.classList.add('tee-magnify-active');
      activeZoomable = zoomable;
    }

    wrapper.addEventListener('mousemove', function (e) {
      if (!desktopMQ.matches) return;
      // Don't fight the sticky/curtain state.
      if (document.body.classList.contains('gallery--sticky')) { reset(); return; }

      var zoomable = e.target.closest ? e.target.closest(ZOOMABLE_SEL) : null;
      if (!zoomable || !wrapper.contains(zoomable)) { reset(); return; }

      pendingZoomable = zoomable;
      pendingX = e.clientX;
      pendingY = e.clientY;
      if (!rafId) rafId = requestAnimationFrame(applyPending);
    });

    wrapper.addEventListener('mouseleave', reset);
    // If teeinblue re-renders mid-hover, the stale node is gone; drop our ref.
    wrapper.addEventListener('mouseout', function (e) {
      if (activeZoomable && !document.contains(activeZoomable)) activeZoomable = null;
    });
  }

  /* ----------------------------------------------------------------------
   * 2. Mobile zoom: one tap to zoom in, then drag to pan the image around
   *    (translate-based, so the image follows your finger like a real pan —
   *    not a magnifier chasing the cursor). Tap again to zoom out.
   * -------------------------------------------------------------------- */
  function setupMobileZoom(wrapper) {
    var startX = 0, startY = 0, lastX = 0, lastY = 0, moved = false;
    var activeZoomable = null;     // the zoomable being touched right now
    var panX = 0, panY = 0;        // current pan offset (px) of the zoomed image

    function innerOf(z) { return z.querySelector(INNER_SEL); }

    function render(z) {
      var inner = innerOf(z);
      if (!inner) return;
      inner.style.transformOrigin = '50% 50%';
      inner.style.transform =
        'translate(' + Math.round(panX) + 'px,' + Math.round(panY) + 'px) scale(' + ZOOM_SCALE + ')';
    }

    // Keep the image edges from being dragged inside the frame.
    function clampPan(z) {
      var r = z.getBoundingClientRect();
      var maxX = (r.width * (ZOOM_SCALE - 1)) / 2;
      var maxY = (r.height * (ZOOM_SCALE - 1)) / 2;
      panX = clamp(panX, -maxX, maxX);
      panY = clamp(panY, -maxY, maxY);
    }

    function zoomIn(z) {
      panX = 0; panY = 0;          // zoom in centered; the user pans from there
      z.classList.add('tee-zoom-active');
      render(z);
    }

    function zoomOut(z) {
      var inner = innerOf(z);
      if (inner) {
        inner.style.removeProperty('transform');
        inner.style.removeProperty('transform-origin');
      }
      z.classList.remove('tee-zoom-active');
      panX = 0; panY = 0;
    }

    wrapper.addEventListener('touchstart', function (e) {
      if (!mobileMQ.matches) return;
      // While the preview is pinned/sticky, the curtain handle owns the UI.
      if (document.body.classList.contains('gallery--sticky')) return;
      var t = e.target;
      var zoomable = t.closest ? t.closest(ZOOMABLE_SEL) : null;
      if (!zoomable || !wrapper.contains(zoomable)) return;
      activeZoomable = zoomable;
      moved = false;
      startX = lastX = e.touches[0].clientX;
      startY = lastY = e.touches[0].clientY;
    }, { passive: true });

    wrapper.addEventListener('touchmove', function (e) {
      if (!activeZoomable) return;
      var x = e.touches[0].clientX, y = e.touches[0].clientY;
      if (Math.abs(x - startX) > 6 || Math.abs(y - startY) > 6) moved = true;

      // Pan only when already zoomed in: translate by the finger's delta so the
      // image moves with the finger.
      if (activeZoomable.classList.contains('tee-zoom-active')) {
        e.preventDefault();
        panX += (x - lastX);
        panY += (y - lastY);
        clampPan(activeZoomable);
        render(activeZoomable);
      }
      lastX = x; lastY = y;
    }, { passive: false });

    wrapper.addEventListener('touchend', function () {
      if (!activeZoomable) return;
      var zoomable = activeZoomable;
      activeZoomable = null;

      if (moved) return; // it was a drag/pan, not a tap

      if (zoomable.classList.contains('tee-zoom-active')) {
        zoomOut(zoomable);   // tap while zoomed -> zoom out
      } else {
        zoomIn(zoomable);    // tap while normal -> zoom in (centered)
      }
    });

    // Leaving mobile (e.g. rotate to landscape desktop width) clears any zoom.
    mobileMQ.addEventListener('change', function (ev) {
      if (!ev.matches) {
        var z = wrapper.querySelector(ZOOMABLE_SEL + '.tee-zoom-active');
        if (z) zoomOut(z);
      }
    });
  }

  /* ----------------------------------------------------------------------
   * 3. Mobile collapse/expand curtain
   *    Hooks the body.gallery--sticky state from tee-sticky-gallery.js.
   * -------------------------------------------------------------------- */
  function setupCurtain(wrapper) {
    var handle = null;
    var collapsed = false;
    var resizeObs = null;

    function previewEl() {
      return wrapper.querySelector('#tee-gallery');
    }

    // --- Header tracking ---------------------------------------------------
    // Keep the pinned preview attached to the theme's sticky header: top:0 when
    // the header is collapsed, top:headerHeight when it expands. The header
    // toggles a `shopify-section-header-hidden` class (top: -headerHeight), so we
    // derive the offset from that class and publish it as --tee-header-offset,
    // which the pinned preview + mask read for their `top`.
    var headerEl = document.querySelector('.section-header');
    var headerObs = null;

    function headerOffset() {
      if (!headerEl || !document.body.contains(headerEl)) return 0;
      if (headerEl.classList.contains('shopify-section-header-hidden')) return 0;
      return clamp(headerEl.offsetHeight || 0, 0, 200);
    }

    function syncHeader() {
      document.documentElement.style.setProperty('--tee-header-offset', headerOffset() + 'px');
      positionHandle();
    }

    function buildHandle() {
      if (handle) return handle;
      handle = document.createElement('button');
      handle.type = 'button';
      handle.className = 'tee-curtain-handle';
      handle.setAttribute('aria-label', 'Toggle preview');
      handle.innerHTML =
        '<span class="tee-curtain-handle__grip"></span>' +
        '<span class="tee-curtain-handle__label"></span>';
      document.body.appendChild(handle);
      wireHandle(handle);
      return handle;
    }

    function setLabel() {
      if (!handle) return;
      var label = handle.querySelector('.tee-curtain-handle__label');
      if (label) label.textContent = collapsed ? '▼' : '▲';
    }

    function positionHandle() {
      if (!handle) return;
      var offset = headerOffset(); // ride down with the header when it expands
      if (collapsed) {
        // Collapsed: preview is fully hidden, so the handle attaches to the top —
        // just under the header (offset) or at the very top of the page.
        handle.style.top = offset + 'px';
        return;
      }
      var el = previewEl();
      if (!el) return;
      // Use the (transform-invariant) rendered HEIGHT, not bottom. When expanded
      // the preview is pinned so bottom === top + height — but during the
      // collapse/expand animation `bottom` is mid-transition while `height` stays
      // constant. Targeting height lets the handle jump straight to its final
      // position so its own CSS top-transition animates down in sync, instead of
      // freezing near the top when pulling the curtain back down.
      var h = Math.round(el.getBoundingClientRect().height);
      handle.style.top = (offset + Math.max(0, h)) + 'px';
    }

    // Apply the current `collapsed` state to the DOM (transform + class + handle).
    function applyState() {
      var el = previewEl();
      document.body.classList.toggle('tee-curtain-collapsed', collapsed);
      if (el) {
        el.style.transformOrigin = 'top center';
        if (collapsed) {
          // Slide the preview fully out of view (up behind the header / off the
          // top). translateY(-110%) is relative to the preview's OWN height, so it
          // always hides it completely and auto-adjusts whenever teeinblue re-fits
          // the gallery — no stale pixel value that could leave a peeking sliver.
          el.style.transform = 'translateY(-110%) scale(0.7)';
        } else {
          el.style.transform = 'scale(0.7)';
        }
      }
      setLabel();
      positionHandle();
    }

    // User toggled the handle — remember the choice for the rest of this page.
    function setCollapsed(next) {
      collapsed = !!next;
      applyState();
    }

    function clearPreviewTransform() {
      var el = previewEl();
      if (el) {
        el.style.removeProperty('transform');
        el.style.removeProperty('transform-origin');
      }
    }

    function wireHandle(h) {
      var startY = null, dragMoved = false;
      h.addEventListener('pointerdown', function (e) {
        startY = e.clientY;
        dragMoved = false;
        try { h.setPointerCapture(e.pointerId); } catch (err) {}
      });
      h.addEventListener('pointermove', function (e) {
        if (startY === null) return;
        if (Math.abs(e.clientY - startY) > 6) dragMoved = true;
      });
      h.addEventListener('pointerup', function (e) {
        if (startY === null) return;
        var dy = e.clientY - startY;
        startY = null;
        if (dragMoved) setCollapsed(dy < 0); // dragged up = collapse
        else setCollapsed(!collapsed);       // tap = toggle
      });
      // Stop the synthetic click so it can't re-toggle.
      h.addEventListener('click', function (e) { e.preventDefault(); });
    }

    function onStickyOn() {
      buildHandle();
      // Re-apply the REMEMBERED collapsed/expanded state. `collapsed` persists
      // for the page, so scrolling past the add-to-cart and back doesn't reset it.
      document.body.classList.remove('tee-curtain-ready');
      syncHeader();   // set the header offset BEFORE ready so it snaps in place
      // Apply the state now (without the transition) so it snaps to the remembered
      // position instead of animating from expanded on every re-activation.
      applyState();
      requestAnimationFrame(function () {
        positionHandle();
        requestAnimationFrame(function () {
          document.body.classList.add('tee-curtain-ready');
        });
      });
      // Follow the header as it collapses/expands while pinned.
      if (headerEl && !headerObs) {
        headerObs = new MutationObserver(syncHeader);
        headerObs.observe(headerEl, { attributes: true, attributeFilter: ['class'] });
      }
      // teeinblue re-fits the preview (changing its height) on the resize events
      // the sticky script dispatches while scrolling. Re-apply the current state
      // on every reflow so the collapse transform is recomputed to the new height
      // (otherwise a stale translateY lets a sliver of the preview peek out) and
      // the handle stays glued.
      var el = previewEl();
      if (el && 'ResizeObserver' in window) {
        resizeObs = new ResizeObserver(function () {
          applyState();
        });
        resizeObs.observe(el);
      }
    }

    function onStickyOff() {
      // Keep `collapsed` as the remembered preference for when sticky re-arms;
      // only drop the visual classes and the inline transform.
      document.body.classList.remove('tee-curtain-ready');
      document.body.classList.remove('tee-curtain-collapsed');
      if (resizeObs) { resizeObs.disconnect(); resizeObs = null; }
      if (headerObs) { headerObs.disconnect(); headerObs = null; }
      document.documentElement.style.removeProperty('--tee-header-offset');
      clearPreviewTransform();
    }

    // Watch the body class that tee-sticky-gallery.js toggles.
    var wasSticky = document.body.classList.contains('gallery--sticky');
    var bodyObserver = new MutationObserver(function () {
      var isSticky = document.body.classList.contains('gallery--sticky');
      if (isSticky === wasSticky) return;
      wasSticky = isSticky;
      if (isSticky) onStickyOn();
      else onStickyOff();
    });
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    // Keep the offset + handle in sync as the sticky layout / header shifts.
    // (syncHeader() also calls positionHandle().)
    window.addEventListener('resize', function () {
      if (document.body.classList.contains('gallery--sticky')) syncHeader();
    });
    window.addEventListener('scroll', function () {
      if (document.body.classList.contains('gallery--sticky')) syncHeader();
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
